import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { toast } from '@/hooks/useToast';
import { shortDateTime } from '../outpatient/ui';
import DispenseRequestDialog from './DispenseRequestDialog';
import { useMarkCollected, useWorklist } from './hooks';
import { PriorityBadge } from './ui';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import type { WardRequest } from './api';

const where = (r: { patient_name: string; ward_name: string | null; bed_number: string | null }) =>
  `${r.patient_name}${r.ward_name ? ` · ${r.ward_name}${r.bed_number ? ` / ${r.bed_number}` : ''}` : ''}`;

/**
 * The IP pharmacy's worklist (multi-store Phase 4, design doc §8.4): what the
 * wards have asked for (STAT, then urgent, then oldest), what is dispensed and
 * waiting to be handed over, and active orders nothing has been sent for yet.
 * Refreshes itself every 20 seconds.
 */
export default function IpWorklist() {
  const list = useWorklist();
  const collected = useMarkCollected();
  const [dispensing, setDispensing] = useState<WardRequest | null>(null);
  const [handingOver, setHandingOver] = useState<WardRequest | null>(null);
  const data = list.data;

  const handOver = (r: WardRequest, who: string) => {
    collected.mutate({ id: r.id, collectedBy: who }, {
      onSuccess: () => { toast({ title: `${r.request_number} handed over to ${who}`, variant: 'success' }); setHandingOver(null); },
    });
  };

  if (list.isLoading) return <Card><div className="p-4 text-[13px] text-ink-muted">Loading the worklist…</div></Card>;
  if (list.isError || !data) {
    return <Card><div className="p-4 text-[13px] text-danger">Could not load the worklist. <Button variant="ghost" size="sm" onClick={() => list.refetch()}>Try again</Button></div></Card>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="mb-2 text-sm font-semibold">Ward requests to dispense ({data.requests.length})</div>
        {!data.requests.length ? (
          <div className="py-4 text-center text-[13px] text-ink-muted">Nothing waiting. Wards raise requests from the IP Case Sheet’s medication chart.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-start gap-3 py-2.5 text-[13px]">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[12px] font-semibold">{r.request_number}</span>
                    <PriorityBadge priority={r.priority} />
                    <span className="font-semibold">{where(r)}</span>
                  </div>
                  <div className="mt-0.5 text-[12.5px]">{r.items.map((i) => `${i.drug_name} × ${i.qty_requested}`).join(' · ')}</div>
                  <div className="mt-0.5 text-[11.5px] text-ink-muted">
                    {shortDateTime(r.requested_at)} · {r.requested_by}{r.notes ? ` · “${r.notes}”` : ''}
                  </div>
                </div>
                <Button size="sm" onClick={() => setDispensing(r)}>Dispense</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-2 text-sm font-semibold">Ready to hand over ({data.ready.length})</div>
        {!data.ready.length ? (
          <div className="py-3 text-center text-[13px] text-ink-muted">Nothing waiting to be collected.</div>
        ) : (
          <div className="divide-y divide-border">
            {data.ready.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 py-2 text-[13px]">
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-[12px] font-semibold">{r.request_number}</span> · {where(r)}
                  <span className="block text-[11.5px] text-ink-muted">Dispensed {shortDateTime(r.dispensed_at)} on {r.bill_number}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { collected.reset(); setHandingOver(r); }}>Handed over…</Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.unsupplied.length > 0 && (
        <Card>
          <div className="mb-1 text-sm font-semibold">Active orders with nothing supplied yet ({data.unsupplied.length})</div>
          <p className="mb-2 text-[12px] text-ink-muted">No ward request and no dispensing so far. Supply ahead from the Counter, or dispense from the patient’s Case Sheet.</p>
          <div className="divide-y divide-border">
            {data.unsupplied.map((o) => (
              <div key={o.medication_order_id} className="py-1.5 text-[12.5px]">
                <span className="font-semibold">{o.drug_name}</span>
                {' '}{[o.dose_quantity && `${o.dose_quantity} ${o.dose_unit ?? ''}`.trim(), o.route, o.frequency_code, o.is_prn ? 'PRN' : null].filter(Boolean).join(' · ')}
                <span className="text-ink-muted"> — {where(o)} · since {shortDateTime(o.start_datetime)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {dispensing && <DispenseRequestDialog request={dispensing} onClose={() => setDispensing(null)} />}
      <TextPromptDialog
        open={!!handingOver}
        title={`Hand over ${handingOver?.request_number ?? ''}`}
        label="Who is taking it to the ward?"
        placeholder="e.g. Ward assistant Ravi, or the attender’s name"
        confirmLabel="Record hand-over"
        pending={collected.isPending}
        error={collected.error instanceof Error ? collected.error.message : null}
        onConfirm={(who) => { if (handingOver) handOver(handingOver, who); }}
        onClose={() => setHandingOver(null)}
      />
    </div>
  );
}
